import { FC, RefObject, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Divider, useTheme, Stack } from "@mui/material";
import { apiUrl, APP_LOGO_WHITE } from "consts";
import { DateTime } from "components/DateTime";
import LuminanceTable from "../tables/LuminanceTable";
import IlluminanceTable from "../tables/IlluminanceTable";
import IlluminanceBordersTable from "../tables/IlluminanceBordersTable";
import ProjectInfo from "./ProjectInfo";
import RoadwayInfo from "./RoadwayInfo";
import LuminaireRow from "./LuminaireRow";
import LuminaireFound from "./LuminaireFound";
import Footer from "./Footer";
import getStyles from "./styles";

export type ProjectConfig = {
  project_id?: string;
  name_of_road?: string;
  comment?: string;
};

export type Project = {
  project_name?: string;
  location?: string;
};

interface OutprintProps {
  selectedResult: string;
  projectConfig: ProjectConfig;
  project: Project;
  data: any;
  article: any;
  displayLuminance: boolean;
  displayIlluminance: boolean;
  luminance: any;
  luminance_summary: any;
  illuminance: any;
  surfaces: any;
  sidewalk_placing: any;
  subVal: any;
  subValResult: any;
  loadperkm: string;
  lampset: string;
  family_image: string;
  lvk_png_url: string; // @deprecated
  lvk_img_url?: string;
  article_number: string;
  var_number?: string;
  light_flux: string;
  power: string;
  delta: string;
  e_state_left: string;
  e_state_right: string;
  lightingClassConfig: any;
  lph: string;
  overhang: string;
  pos: string;
  s_state: string;
  tilt: string;
  maintenanceFactor: string | number;
  roadLayoutConfig: any;
  roadwayConfig: any;
  resultPageRef: RefObject<HTMLDivElement>;
  pageNumbers?: number;
  ulor: number;
  n_lums?: number;
  f_ti?: number;
}

const prefix = "res_roads/res_road";

const Outprint: FC<OutprintProps> = ({
  selectedResult,
  projectConfig,
  project,
  data,
  article,
  displayLuminance,
  displayIlluminance,
  luminance,
  luminance_summary,
  illuminance,
  surfaces,
  sidewalk_placing,
  subVal,
  subValResult,
  loadperkm,
  lampset,
  family_image,
  lvk_png_url,
  lvk_img_url,
  article_number,
  var_number,
  light_flux,
  power,
  delta,
  e_state_left,
  e_state_right,
  lightingClassConfig,
  lph,
  overhang,
  pos,
  s_state,
  tilt,
  maintenanceFactor,
  roadLayoutConfig,
  roadwayConfig,
  resultPageRef,
  ulor,
  n_lums,
  f_ti,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).Outprint, [theme]);

  const hasData = !!selectedResult;
  if (!hasData) {
    return null;
  }

  return (
    <Box id="outprint-document" ref={resultPageRef} sx={styles.container}>
      <Box component="figure" sx={styles.logo}>
        <Box component="img" src={APP_LOGO_WHITE} alt="Logo" />
        <Box component="figcaption">Logo</Box>
      </Box>

      <Box sx={styles.content}>
        <Box component="header" sx={styles.header}>
          <Stack sx={styles.title}>
            <Typography variant="h1" fontSize="16pt">
              {t("Lighting Calculation")}
            </Typography>
            <DateTime />
          </Stack>

          <ProjectInfo projectConfig={projectConfig} project={project} />
        </Box>

        {data && article !== null && (
          <Box>
            <Stack sx={styles.technical}>
              <RoadwayInfo pos={pos} roadLayoutConfig={roadLayoutConfig} roadwayConfig={roadwayConfig} />

              <Divider orientation="vertical" flexItem sx={{ m: 0, p: 0 }} />

              <LuminaireRow
                delta={delta}
                lph={lph}
                overhang={overhang}
                pos={pos}
                tilt={tilt}
                maintenanceFactor={maintenanceFactor.toString()}
                n_lums={n_lums ?? null}
                roadwayConfig={roadwayConfig}
              />
            </Stack>

            <LuminaireFound
              loadPerKm={loadperkm}
              lampset={lampset}
              familyImage={family_image}
              lvkPngUrl={lvk_img_url ? `${apiUrl}${lvk_img_url}` : lvk_png_url}
              articleNumber={article_number}
              varNumber={var_number}
              lightFlux={light_flux}
              power={power}
            />

            <Box
              sx={{
                pageBreakBefore: "always",
                breakBefore: "page",
                mt: "5mm",
              }}
            >
              {displayLuminance &&
                Object.keys(luminance).map((key: string, index: number) => (
                  <LuminanceTable
                    key={key}
                    lightingClassConfig={lightingClassConfig}
                    luminance={(luminance as Record<string, unknown>)[key]}
                    luminance_summary={luminance_summary}
                    illuminance={illuminance}
                    surfaces={surfaces}
                    sidewalk_placing={sidewalk_placing}
                    divId={`heatmaproot${key}-print`}
                    displayHeader={key === "0"}
                    ulor={ulor}
                    e_state_left={e_state_left}
                    s_state={s_state}
                    e_state_right={e_state_right}
                    roadLayoutConfig={roadLayoutConfig}
                    index={index}
                    showLane={Object.keys(luminance).length > 1}
                  />
                ))}
              {displayIlluminance && (
                <IlluminanceTable
                  lightingClassConfig={lightingClassConfig}
                  illuminance={illuminance}
                  surfaces={surfaces}
                  sidewalk_placing={sidewalk_placing}
                  subVal={subVal}
                  subValResult={subValResult}
                  divId="print-illuminance"
                  ulor={ulor}
                  s_state={s_state}
                  e_state_left={e_state_left}
                  e_state_right={e_state_right}
                  roadLayoutConfig={roadLayoutConfig}
                  f_ti={f_ti}
                />
              )}
              {displayLuminance && sidewalk_placing !== null && (
                <IlluminanceBordersTable
                  lightingClassConfig={lightingClassConfig}
                  illuminance={illuminance}
                  surfaces={surfaces}
                  sidewalk_placing={sidewalk_placing}
                  subVal={subVal}
                  subValResult={subValResult}
                  divId="print"
                  ulor={ulor}
                  f_ti={f_ti}
                />
              )}
            </Box>
          </Box>
        )}

        <Footer />
      </Box>
    </Box>
  );
};

export default Outprint;
